import { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Bold,
  Italic,
  Heading2,
  Heading3,
  Quote,
  List,
  ListOrdered,
  Image as ImageIcon,
  Link2,
  Link2Off,
  Undo2,
  Redo2,
  Minus,
} from "lucide-react";
import { toast } from "sonner";
import { api, adminHeaders } from "@/lib/api";

/**
 * Rich-text editor for The Cabbage Chronicle.
 * Uses Tiptap (ProseMirror) + the StarterKit. Outputs HTML which we store
 * directly. Inline images are uploaded to /api/admin/chronicle/upload-image
 * and embedded as <img> with the returned URL.
 */
export default function RichTextEditor({
  value,
  onChange,
  placeholder = "Start writing\u2026",
}) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        bulletList: { keepMarks: true },
        orderedList: { keepMarks: true },
        blockquote: { HTMLAttributes: { class: "chronicle-quote" } },
        codeBlock: false,
      }),
      Link.configure({
        autolink: true,
        openOnClick: false,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
      Image.configure({
        HTMLAttributes: { class: "chronicle-inline-image" },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    onUpdate: ({ editor: ed }) => {
      onChange?.(ed.getHTML());
    },
    editorProps: {
      attributes: {
        class:
          "chronicle-prose min-h-[420px] focus:outline-none px-5 py-6 md:px-8 md:py-8",
      },
    },
  });

  // Keep editor content in sync when parent prop changes (e.g. switching post)
  useEffect(() => {
    if (!editor) return;
    if (value !== editor.getHTML()) {
      editor.commands.setContent(value || "", { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  const onAddLink = () => {
    const previous = editor.getAttributes("link").href || "";
    const url = window.prompt("Link URL", previous);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const onAddImage = async () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const fd = new FormData();
      fd.append("file", file);
      try {
        const r = await api.post("/admin/chronicle/upload-image", fd, {
          headers: {
            ...adminHeaders(),
            "Content-Type": "multipart/form-data",
          },
        });
        if (r.data?.url) {
          editor.chain().focus().setImage({ src: r.data.url }).run();
        }
      } catch {
        toast.error("Image upload failed");
      }
    };
    input.click();
  };

  const Btn = ({ onClick, active, disabled, label, children }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className={`flex h-9 w-9 items-center justify-center border-2 border-[#1A3626] transition-all md:h-10 md:w-10 ${
        active
          ? "bg-[#1A3626] text-[#F9F9F4]"
          : "bg-[#F9F9F4] text-[#1A3626] hover:bg-[#EBE7DD]"
      } disabled:cursor-not-allowed disabled:opacity-40`}
      data-testid={`rte-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {children}
    </button>
  );

  return (
    <div className="border-2 border-[#1A3626] bg-[#F9F9F4]" data-testid="rich-text-editor">
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-1.5 border-b-2 border-[#1A3626] bg-[#EBE7DD] p-2 md:gap-2">
        <Btn label="Bold" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}>
          <Bold className="h-4 w-4" strokeWidth={2.6} />
        </Btn>
        <Btn label="Italic" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}>
          <Italic className="h-4 w-4" strokeWidth={2.6} />
        </Btn>
        <span className="mx-1 h-6 w-px bg-[#1A3626]/30" aria-hidden />
        <Btn label="Heading 2" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
          <Heading2 className="h-4 w-4" strokeWidth={2.6} />
        </Btn>
        <Btn label="Heading 3" active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
          <Heading3 className="h-4 w-4" strokeWidth={2.6} />
        </Btn>
        <span className="mx-1 h-6 w-px bg-[#1A3626]/30" aria-hidden />
        <Btn label="Bullet List" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <List className="h-4 w-4" strokeWidth={2.6} />
        </Btn>
        <Btn label="Numbered List" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          <ListOrdered className="h-4 w-4" strokeWidth={2.6} />
        </Btn>
        <Btn label="Quote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
          <Quote className="h-4 w-4" strokeWidth={2.6} />
        </Btn>
        <Btn label="Divider" onClick={() => editor.chain().focus().setHorizontalRule().run()}>
          <Minus className="h-4 w-4" strokeWidth={2.6} />
        </Btn>
        <span className="mx-1 h-6 w-px bg-[#1A3626]/30" aria-hidden />
        <Btn label="Add Link" active={editor.isActive("link")} onClick={onAddLink}>
          <Link2 className="h-4 w-4" strokeWidth={2.6} />
        </Btn>
        <Btn label="Remove Link" disabled={!editor.isActive("link")} onClick={() => editor.chain().focus().unsetLink().run()}>
          <Link2Off className="h-4 w-4" strokeWidth={2.6} />
        </Btn>
        <Btn label="Insert Image" onClick={onAddImage}>
          <ImageIcon className="h-4 w-4" strokeWidth={2.6} />
        </Btn>
        <span className="ml-auto flex gap-1.5 md:gap-2">
          <Btn label="Undo" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()}>
            <Undo2 className="h-4 w-4" strokeWidth={2.6} />
          </Btn>
          <Btn label="Redo" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()}>
            <Redo2 className="h-4 w-4" strokeWidth={2.6} />
          </Btn>
        </span>
      </div>

      <EditorContent editor={editor} />

      <style>{`
        .chronicle-prose { font-family: inherit; color: #1A3626; }
        .chronicle-prose p { font-size: 17px; line-height: 1.75; margin: 0 0 1.1em; }
        .chronicle-prose h2 { font-family: var(--font-display, inherit); font-size: 32px; line-height: 1.1; font-weight: 900; letter-spacing: -0.01em; margin: 1.4em 0 0.5em; text-transform: uppercase; }
        .chronicle-prose h3 { font-family: var(--font-display, inherit); font-size: 22px; line-height: 1.2; font-weight: 900; letter-spacing: -0.005em; margin: 1.3em 0 0.4em; text-transform: uppercase; }
        .chronicle-prose ul, .chronicle-prose ol { padding-left: 1.4em; margin: 0 0 1.1em; }
        .chronicle-prose li { font-size: 17px; line-height: 1.7; margin-bottom: 0.3em; }
        .chronicle-prose blockquote, .chronicle-prose .chronicle-quote {
          border-left: 3px solid #93C065; margin: 1.4em 0; padding: 0.4em 0 0.4em 1.1em; font-style: italic; color: #1A3626;
        }
        .chronicle-prose a { color: #1A3626; text-decoration: underline; text-decoration-color: #93C065; text-decoration-thickness: 2px; text-underline-offset: 3px; }
        .chronicle-prose img.chronicle-inline-image, .chronicle-prose img {
          max-width: 100%; height: auto; display: block; margin: 1.6em 0; border: 2px solid #1A3626;
        }
        .chronicle-prose hr { border: 0; border-top: 2px solid #1A3626; margin: 2em 0; }
        .chronicle-prose p.is-editor-empty:first-child::before {
          content: attr(data-placeholder); color: #1A3626; opacity: .4; float: left; height: 0; pointer-events: none;
        }
      `}</style>
    </div>
  );
}
